'use client'

import { useAbTest, useTrackAbTest } from '@marketplace-web/shared/ab-tests'
import { reloadPage, useWindowEvent } from '@marketplace-web/shared/browser'
import { useLatestCallback } from '@marketplace-web/shared/ui-helpers'

import { DisplayType, OptionType, SelectionType } from 'constants/dynamic-filters'
import { Screen } from 'constants/tracking/screens'
import { DynamicFilterModel, SelectedDynamicFilterModel } from 'types/models/dynamic-filter'

import { useCatalogContext } from 'pages/Catalog/CatalogProvider'
import useFetchCatalogBrand from 'pages/Catalog/hooks/useFetchCatalogBrand'

import GridFilterDropdown from '../common/DynamicFilters/GridFilter/GridFilterDropdown'
import ListFilterDropdown from '../common/DynamicFilters/ListFilter/ListFilterDropdown'
import SearchFilterDropdown from '../common/DynamicFilters/SearchFilter/SearchFilterDropdown'
import useFetchDynamicFilters from '../../hooks/useFetchDynamicFilters'
import { addSelectedFilter, removeSelectedFilter } from '../../utils/utils'
import CatalogFilter from './CatalogFilter'
import { CatalogFilterFacetedDropdown } from './CatalogFilterFaceted'
import PriceFilter from './PriceFilter'
import SortFilter from './SortFilter'

type Props = {
  learnMoreUrl?: string | null
}

const Filters = ({ learnMoreUrl }: Props) => {
  const {
    state: {
      dynamicFilters: { dynamicFilters, selectedDynamicFilters },
    },
  } = useCatalogContext()
  const fetchDynamicFilters = useFetchDynamicFilters()
  const fetchCatalogBrand = useFetchCatalogBrand()

  const holdoutQ4 = useAbTest('buyer_domain_holdout_2024q4')?.variant !== 'off'
  const facetedCategoriesAbTest = useAbTest('scoped_search_faceted_categories_v2')
  const isFacetedCategoriesEnabled = facetedCategoriesAbTest?.variant === 'on' && holdoutQ4
  // this will only expose in desktop, as in mobile this component does not render
  useTrackAbTest(facetedCategoriesAbTest, holdoutQ4)

  useWindowEvent('popstate', reloadPage)

  const handleFilterUpdate = useLatestCallback(
    (updatedSelectedFilters: Array<SelectedDynamicFilterModel>) => {
      fetchDynamicFilters({ selectedDynamicFilters: updatedSelectedFilters })
      fetchCatalogBrand({ selectedDynamicFilters: updatedSelectedFilters })
    },
  )

  const handleAddSelectedFilter = useLatestCallback(
    (newFilter: { type: string; ids: Array<number> }) => {
      const updatedSelectedFilters = addSelectedFilter(newFilter, selectedDynamicFilters)

      handleFilterUpdate(updatedSelectedFilters)
    },
  )

  const handleRemoveSelectedFilter = useLatestCallback(
    (removeFilter: { type: string; id: number }) => {
      const updatedSelectedFilters = removeSelectedFilter(removeFilter, selectedDynamicFilters)

      handleFilterUpdate(updatedSelectedFilters)
    },
  )

  const handleDynamicFilterToggle = useLatestCallback(
    (id: number, type: string, selectionType: SelectionType, selectedIds: Array<number>) => {
      if (selectedIds.includes(id)) {
        if (selectionType === SelectionType.Multi) {
          handleRemoveSelectedFilter({ type, id })
        }
      } else {
        const newIds = selectionType === SelectionType.Single ? [id] : [...selectedIds, id]
        handleAddSelectedFilter({ type, ids: newIds })
      }
    },
  )

  const getRootOptionType = (dynamicFilter: DynamicFilterModel) => {
    return dynamicFilter.options.find(option => !!option.type)?.type || OptionType.Default
  }

  const getSelectedFilter = (type: string) => {
    return selectedDynamicFilters.find(filter => filter.type === type)
  }

  const renderDynamicFilter = (filter: DynamicFilterModel) => {
    const selectedDynamicFilter = getSelectedFilter(filter.type)
    const key = `${filter.type}-${filter.id}`

    switch (filter.displayType) {
      case DisplayType.List: {
        return (
          <ListFilterDropdown
            key={key}
            type={filter.type}
            title={filter.title}
            selectionType={filter.selectionType}
            isSelectionHighlighted={filter.isSelectionHighlighted}
            isHidden={filter.isHidden}
            experiment={filter.experiment}
            links={filter.links}
            rootOptionType={getRootOptionType(filter)}
            options={filter.options}
            fromScreen={Screen.Catalog}
            selectedIds={selectedDynamicFilter?.ids || []}
            onListOptionSelect={id =>
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                selectedDynamicFilter?.ids || [],
              )
            }
          />
        )
      }
      case DisplayType.ListSearch: {
        return (
          <SearchFilterDropdown
            key={key}
            type={filter.type}
            title={filter.title}
            selectionType={filter.selectionType}
            isSelectionHighlighted={filter.isSelectionHighlighted}
            isHidden={filter.isHidden}
            experiment={filter.experiment}
            links={filter.links}
            rootOptionType={getRootOptionType(filter)}
            options={filter.options}
            fromScreen={Screen.Catalog}
            translations={filter.searchTranslations}
            selectedIds={selectedDynamicFilter?.ids || []}
            onListOptionSelect={id =>
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                selectedDynamicFilter?.ids || [],
              )
            }
          />
        )
      }
      case DisplayType.Grid: {
        return (
          <GridFilterDropdown
            key={key}
            type={filter.type}
            title={filter.title}
            fromScreen={Screen.Catalog}
            isSelectionHighlighted={filter.isSelectionHighlighted || false}
            isHidden={filter.isHidden}
            experiment={filter.experiment}
            rootOptionType={getRootOptionType(filter)}
            options={filter.options}
            selectedIds={selectedDynamicFilter?.ids || []}
            onListOptionSelect={id =>
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                selectedDynamicFilter?.ids || [],
              )
            }
          />
        )
      }
      case DisplayType.HybridPrice: {
        return <PriceFilter key={key} minimumValidation={filter.minimumValidation} />
      }
      default:
        return null
    }
  }

  const renderDynamicFilters = () => {
    return dynamicFilters.map(filter => renderDynamicFilter(filter))
  }

  return (
    <div className="u-flexbox u-flex-wrap">
      {isFacetedCategoriesEnabled ? <CatalogFilterFacetedDropdown /> : <CatalogFilter />}
      {renderDynamicFilters()}
      <SortFilter learnMoreUrl={learnMoreUrl} />
    </div>
  )
}

export default Filters
